import { Component, computed, inject, signal } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import packageInfo from '../../package.json';
import { Icon } from './icon';
import { PersonPicture } from './person-picture';
import { RecordDocuments } from './record-documents';
import { Dialog } from './dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { I18nService } from './i18n.service';
import { ThemeService } from './theme.service';
import { allSections, configurationSections, Field, RecordData, reportSections, Section, sections } from './management.config';

interface AccessSession {
  username: string;
  role: 'OPERATOR' | 'SUPER_ADMIN' | 'COMPANY' | 'INSTITUTION' | 'MEMBER';
  institutionId: number | null;
  institutionName: string | null;
  institutionRole?: 'ADMIN' | 'USER' | null;
  memberId?: number | null;
  companyId?: number | null;
  companyName?: string | null;
}

interface MemberPortalData {
  member: RecordData;
  claims: RecordData[];
  guaranteeLetters: RecordData[];
}

interface CompanyGuaranteeDraft {
  name: string;
  category: string;
  riskType: string;
  reimbursementRate: number | null;
  socialSecurityReimbursementRate: number | null;
  memberReimbursementRate: number | null;
  memberTaxRate: number | null;
  companyTaxRate: number | null;
  capType: string;
  maxAmount: number | null;
  maxQuantity: number | null;
}

@Component({
  imports: [FormsModule, Icon, PersonPicture, RecordDocuments, Dialog, TranslatePipe, NgTemplateOutlet],
  selector: 'app-root',
  styleUrl: './app.css',
  templateUrl: './app.html',
})
export class App {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  readonly i18n = inject(I18nService);
  readonly theme = inject(ThemeService);
  readonly themeOptions = [
    { value: 'light', label: 'Light', icon: 'sun' },
    { value: 'dark', label: 'Dark', icon: 'moon' },
    { value: 'system', label: 'System', icon: 'monitor' },
    { value: 'ocean', label: 'Ocean', icon: 'waves' },
    { value: 'rose', label: 'Rose', icon: 'flower' },
    { value: 'high-contrast', label: 'High contrast', icon: 'contrast' },
  ] as const;
  readonly languageOptions = [
    { value: 'fr', label: 'French', flag: '\u{1F1EB}\u{1F1F7}' },
    { value: 'en', label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
    { value: 'wo', label: 'Wolof', flag: '\u{1F1F8}\u{1F1F3}' },
  ] as const;
  readonly salaryYears = Array.from({ length: 10 }, (_, index) => new Date().getFullYear() - index);
  readonly salaryCopyYears = Array.from({ length: 11 }, (_, index) => new Date().getFullYear() - index);
  readonly salaryMonths = computed(() => Array.from({ length: 12 }, (_, index) => ({
    value: index + 1,
    label: new Intl.DateTimeFormat(this.i18n.locale(), { month: 'long', timeZone: 'UTC' })
      .format(new Date(Date.UTC(2026, index, 1))),
  })));
  themeIcon(): string { return this.themeOptions.find(option => option.value === this.theme.theme())?.icon ?? 'sun'; }
  languageFlag(): string { return this.languageOptions.find(option => option.value === this.i18n.language())?.flag ?? ''; }
  private readonly initialReportDate = this.today();
  readonly session = signal<AccessSession | null>(null);
  readonly passwordVisible = signal(false);
  readonly memberPortal = signal<MemberPortalData | null>(null);
  readonly memberPortalTab = signal<'member' | 'dependents' | 'claims' | 'letters'>('member');
  readonly memberPortalPage = signal(1);
  readonly memberPortalRows = computed(() => {
    const tab = this.memberPortalTab();
    const rows = tab === 'dependents' ? this.memberDependents()
      : tab === 'claims' ? this.memberPortalClaims() : this.memberPortalLetters();
    const dateKey = tab === 'dependents' ? 'birthDate' : tab === 'claims' ? 'treatmentDate' : 'issueDate';
    return [...rows].sort((left, right) => String(right[dateKey] ?? '').localeCompare(String(left[dateKey] ?? ''))
      || Number(right.id ?? 0) - Number(left.id ?? 0));
  });
  readonly memberPortalPageCount = computed(() => Math.max(1, Math.ceil(this.memberPortalRows().length / 10)));
  readonly memberPortalVisibleRows = computed(() => this.memberPortalRows().slice(
    (this.memberPortalPage() - 1) * 10, this.memberPortalPage() * 10));
  readonly sections = computed(() => {
    const session = this.session();
    if (session?.role === 'MEMBER') return [];
    if (session?.role === 'COMPANY') return sections.filter(section => ['members', 'companies', 'contracts'].includes(section.key));
    if (session?.role === 'SUPER_ADMIN') return sections.filter(section => ['companies', 'providers', 'contracts'].includes(section.key))
      .map(section => section.key === 'providers' ? { ...section, label: 'Institutions', singular: 'institution' } : section);
    return sections.filter(section => session?.role === 'INSTITUTION' && session.institutionRole !== 'ADMIN'
      ? ['members', 'contracts', 'providers'].includes(section.key)
      : !['dependents', 'claims', 'letters'].includes(section.key) && (section.key !== 'companies' || session?.role === 'OPERATOR'));
  });
  readonly reports = computed(() => {
    const session = this.session();
    return session && (session.role === 'OPERATOR' || session.institutionRole === 'ADMIN') ? reportSections : [];
  });
  readonly canViewContributionReport = computed(() => {
    const session = this.session();
    return session?.role === 'OPERATOR' || session?.role === 'SUPER_ADMIN'
      || (session?.role === 'INSTITUTION' && session.institutionRole === 'ADMIN');
  });
  readonly processingActions = computed(() => {
    const session = this.session();
    return session?.role === 'INSTITUTION' && session.institutionRole === 'ADMIN'
      ? [
        { key: 'generate-contributions', label: 'Generate contributions', icon: 'wallet' },
        { key: 'copy-salaries', label: 'Copy salaries', icon: 'clipboard' },
      ] : [];
  });
  readonly processingOpen = signal(false);
  readonly configurationSections = configurationSections;
  readonly canConfigure = computed(() => ['OPERATOR', 'SUPER_ADMIN'].includes(this.session()?.role ?? '')
    || (this.session()?.role === 'INSTITUTION' && this.session()?.institutionRole === 'ADMIN'));
  readonly configurationOpen = computed(() => !this.processingOpen() && !this.contributionReportOpen()
    && configurationSections.some(section => section.key === this.section().key));
  readonly contributionReportOpen = signal(false);
  readonly processingAction = signal('generate-contributions');
  readonly batchHistory = signal<RecordData[]>([]);
  readonly visibleBatchHistory = computed(() => {
    const type = this.processingAction() === 'generate-contributions' ? 'contribution'
      : this.processingAction() === 'copy-salaries' ? 'salary-copy' : null;
    return type ? this.batchHistory().filter(job => job['type'] === type) : [];
  });
  readonly batchHistoryLoading = signal(false);
  readonly batchHistoryError = signal('');
  private readonly currentSalaryPeriod = new Date();
  readonly salaryCopySourceYear = signal(this.currentSalaryPeriod.getMonth() === 0
    ? this.currentSalaryPeriod.getFullYear() - 1 : this.currentSalaryPeriod.getFullYear());
  readonly salaryCopySourceMonth = signal(this.currentSalaryPeriod.getMonth() === 0
    ? 12 : this.currentSalaryPeriod.getMonth());
  readonly salaryCopyTargetYear = signal(this.currentSalaryPeriod.getFullYear());
  readonly salaryCopyTargetMonth = signal(this.currentSalaryPeriod.getMonth() + 1);
  readonly salaryCopySamePeriod = computed(() => this.salaryCopySourceYear() === this.salaryCopyTargetYear()
    && this.salaryCopySourceMonth() === this.salaryCopyTargetMonth());
  readonly salaryCopyJob = signal<RecordData | null>(null);
  readonly salaryCopyJobStarting = signal(false);
  readonly salaryCopyJobRefreshing = signal(false);
  readonly salaryCopyJobRunning = computed(() => this.salaryCopyJob()?.['status'] === 'RUNNING');
  readonly contributionPeriodYear = signal(new Date().getFullYear());
  readonly contributionPeriodMonth = signal(new Date().getMonth() + 1);
  readonly contributionJob = signal<RecordData | null>(null);
  readonly contributionJobStarting = signal(false);
  readonly contributionJobRefreshing = signal(false);
  readonly contributionJobRunning = computed(() => this.contributionJob()?.['status'] === 'RUNNING');
  readonly contributionReportYear = signal(new Date().getFullYear());
  readonly contributionReportMonth = signal(new Date().getMonth() + 1);
  readonly contributionReportRows = signal<RecordData[]>([]);
  readonly contributionReportBusy = signal(false);
  readonly contributionReportLoaded = signal(false);
  readonly contributionReportError = signal('');
  readonly reportStartDate = signal(this.initialReportDate);
  readonly reportEndDate = signal('');
  readonly reportStartDateText = signal(this.formatReportDate(this.initialReportDate));
  readonly reportEndDateText = signal('');
  readonly reportDateError = signal('');
  readonly downloadingReport = signal(false);
  readonly reportDateKey = computed(() => ({
    'report-ledger': 'timestamp',
    'report-audit': 'createdDate',
    'report-guarantee-letters': 'issueDate',
    'report-claims': 'treatmentDate',
  } as Record<string, string>)[this.section().key] ?? null);
  readonly section = signal(sections[0]);
  readonly connected = signal(false);
  readonly loginVisible = signal(false);
  readonly accountDetailsVisible = signal(false);
  readonly applicationVersion = packageInfo.version;
  readonly busy = signal(false);
  readonly rows = signal<RecordData[]>([]);
  readonly lookups = signal<Record<string, RecordData[]>>({});
  readonly query = signal('');
  readonly page = signal(1);
  readonly error = signal('');
  readonly errorParams = signal<Record<string, unknown>>({});
  readonly notice = signal('');
  readonly editor = signal(false);
  readonly editorContext = signal<{ section: Section; member: RecordData } | null>(null);
  readonly editorSection = computed(() => this.editorContext()?.section ?? this.section());
  readonly editorFieldGroups = computed(() => this.fieldGroups(this.editorSection()));
  readonly editingId = signal<number | null>(null);
  readonly detail = signal<RecordData | null>(null);
  readonly recordDetail = signal<{ section: Section; row: RecordData } | null>(null);
  readonly contractGuaranteeDetail = signal<RecordData | null>(null);
  readonly guaranteeDetail = signal<{ company: RecordData; guarantee: RecordData } | null>(null);
  readonly pendingGuaranteeDelete = signal<{ company: RecordData; guarantee: RecordData } | null>(null);
  readonly companyMembers = signal<RecordData[]>([]);
  readonly companyTab = signal<'guarantees' | 'members' | 'salaries'>('guarantees');
  readonly companyMembersBusy = signal(false);
  readonly companyMembersError = signal('');
  readonly companyMembersPage = signal(1);
  readonly companyMembersPageCount = computed(() => Math.max(1, Math.ceil(this.companyMembers().length / 15)));
  readonly visibleCompanyMembers = computed(() => this.companyMembers().slice((this.companyMembersPage() - 1) * 15, this.companyMembersPage() * 15));
  readonly companySalaries = signal<RecordData[]>([]);
  readonly companySalariesBusy = signal(false);
  readonly companySalariesLoaded = signal(false);
  readonly companySalariesError = signal('');
  readonly companySalarySaving = signal(false);
  readonly companySalaryError = signal('');
  readonly companySalaryEditing = signal<number | null>(null);
  readonly companySalaryPage = signal(1);
  readonly companySalaryPageCount = computed(() => Math.max(1, Math.ceil(this.companySalaries().length / 15)));
  readonly visibleCompanySalaries = computed(() => this.companySalaries().slice((this.companySalaryPage() - 1) * 15, this.companySalaryPage() * 15));
  readonly companySalaryDraft = {
    memberId: null as number | null,
    periodYear: new Date().getFullYear(),
    periodMonth: new Date().getMonth() + 1,
    grossSalary: null as number | null,
  };
  readonly recordDetailGroups = computed(() => {
    const detail = this.recordDetail();
    return detail ? this.fieldGroups(detail.section) : [];
  });
  readonly memberDetail = signal<RecordData | null>(null);
  readonly memberDetailsExpanded = signal(false);
  readonly dependentDetail = signal<{ member: RecordData; dependent: RecordData } | null>(null);
  readonly dependentDetailsExpanded = signal(false);
  readonly dependentRecordTab = signal<'claims' | 'letters'>('claims');
  readonly letterDetail = signal<{ member: RecordData; letter: RecordData } | null>(null);
  readonly issuedGuaranteeLinesEditing = signal<number | null>(null);
  issuedGuaranteeLineDrafts: RecordData[] = [];
  readonly printingLetter = signal<number | null>(null);
  readonly memberTab = signal<'dependents' | 'claims' | 'letters' | 'contributions' | 'salaries'>('dependents');
  readonly memberContributions = signal<RecordData[]>([]);
  readonly memberContributionsBusy = signal(false);
  readonly memberContributionsLoaded = signal(false);
  readonly memberContributionsError = signal('');
  readonly canPayContributions = computed(() => this.session()?.role === 'OPERATOR'
    || (this.session()?.role === 'INSTITUTION' && this.session()?.institutionRole === 'ADMIN'));
  readonly pendingContributionPayment = signal<{ memberId: number; contribution: RecordData; cancel?: boolean; cancelContribution?: boolean } | null>(null);
  readonly memberContributionPaying = signal<number | null>(null);
  readonly memberContributionPaymentError = signal('');
  readonly memberClaims = signal<RecordData[]>([]);
  readonly memberClaimsBusy = signal(false);
  readonly memberClaimsLoaded = signal(false);
  readonly memberClaimsError = signal('');
  readonly dependentClaims = computed(() => {
    const dependent = this.dependentDetail()?.dependent;
    const claims = this.session()?.role === 'MEMBER' ? this.memberPortal()?.claims ?? [] : this.memberClaims();
    return dependent ? claims.filter(claim => String(claim['dependentId']) === String(dependent.id)) : [];
  });
  readonly claimPaymentMethod = signal('');
  readonly claimPaymentDesk = signal('');
  readonly claimBankName = signal('');
  readonly claimBankBic = signal('');
  readonly claimBanksLoading = signal(false);
  readonly claimBanks = signal<RecordData[]>([]);
  private claimBankInstitutionId: number | null = null;
  readonly claimBanksError = signal('');
  readonly claimBankAccountNumber = signal('');
  readonly claimMobileNumber = signal('');
  readonly claimPaymentReference = signal('');
  readonly guaranteeLetterPaymentFormId = signal<number | null>(null);
  readonly guaranteeLetterPaymentMethod = signal('');
  readonly guaranteeLetterPaymentBankId = signal<number | null>(null);
  readonly guaranteeLetterPaymentDeskId = signal<number | null>(null);
  readonly guaranteeLetterPaymentReference = signal('');
  readonly guaranteeLetterPaymentBanks = signal<RecordData[]>([]);
  readonly guaranteeLetterPaymentDesks = signal<RecordData[]>([]);
  readonly guaranteeLetterPaymentLookupsLoading = signal(false);
  readonly memberLetters = signal<RecordData[]>([]);
  readonly memberLettersBusy = signal(false);
  readonly memberLettersLoaded = signal(false);
  readonly memberLettersError = signal('');
  readonly dependentGuaranteeLetters = computed(() => {
    const dependent = this.dependentDetail()?.dependent;
    const letters = this.session()?.role === 'MEMBER' ? this.memberPortal()?.guaranteeLetters ?? [] : this.memberLetters();
    return dependent ? letters.filter(letter => String(letter['dependentId']) === String(dependent.id)) : [];
  });
  readonly memberSalaries = signal<RecordData[]>([]);
  readonly memberSalariesBusy = signal(false);
  readonly memberSalariesLoaded = signal(false);
  readonly memberSalariesError = signal('');
  readonly memberSalarySaving = signal(false);
  readonly memberSalaryError = signal('');
  readonly memberSalaryEditing = signal<number | null>(null);
  readonly memberSalaryFormOpen = signal(false);
  readonly memberSalaryDraft = {
    periodYear: new Date().getFullYear(),
    periodMonth: new Date().getMonth() + 1,
    grossSalary: null as number | null,
  };
  readonly pendingDelete = signal<RecordData | null>(null);
  readonly filtered = computed(() => {
    const query = this.query().trim().toLowerCase();
    return this.rows().filter(row => this.reportDateKey() !== null || !query
      || this.section().columns.some(column => this.cell(row, column.key).toLowerCase().includes(query)));
  });
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.filtered().length / 15)));
  readonly visible = computed(() => this.filtered().slice((this.page() - 1) * 15, this.page() * 15));
  credentials = { username: '', password: '' };
  memberId: number | null = null;
  draft: RecordData = {};
  claimLines: RecordData[] = [];
  newGuarantees: CompanyGuaranteeDraft[] = [];
  readonly guaranteeRiskTypes = ['HEALTH', 'DISABILITY', 'LIFE_INSURANCE', 'RETIREMENT'];
  readonly guaranteeCapTypes = ['ANNUAL_BUDGET', 'PER_ACT_MAX', 'QUANTITY_PER_YEAR'];
  protected authorization = '';
  private revision = 0;
  private companyMemberRevision = 0;
  private companySalaryRevision = 0;
  private memberDetailRevision = 0;
  private contributionJobRevision = 0;
  private contributionJobPollTimer: ReturnType<typeof setTimeout> | null = null;
  private salaryCopyJobRevision = 0;
  private salaryCopyJobPollTimer: ReturnType<typeof setTimeout> | null = null;
  private batchHistoryRevision = 0;
  private batchHistoryPollTimer: ReturnType<typeof setTimeout> | null = null;

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    return firstValueFrom(this.http.request<T>(method, '/api/v1' + path, {
      body, headers: new HttpHeaders({ Authorization: this.authorization }),
    }));
  }

  async connect(): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    this.connected.set(false);
    this.session.set(null);
    this.memberPortal.set(null);
    this.rows.set([]);
    this.lookups.set({});
    this.claimBanks.set([]);
    this.claimBankInstitutionId = null;
    try {
      const bytes = new TextEncoder().encode(`${this.credentials.username}:${this.credentials.password}`);
      this.authorization = 'Basic ' + btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
      const session = await this.request<AccessSession>('GET', '/session');
      if (session.role === 'MEMBER') {
        const portal = await this.request<MemberPortalData>('GET', '/member/portal');
        this.session.set(session);
        this.memberPortal.set(portal);
        this.lookups.set({ members: [portal.member] });
        this.memberPortalTab.set('member');
        this.section.set(sections[0]);
        this.connected.set(true);
        this.credentials.password = '';
        return;
      }
      const section = session.role === 'SUPER_ADMIN' ? sections.find(section => section.key === 'companies')! : sections[0];
      const rows = await this.request<RecordData[]>('GET', section.path);
      this.session.set(session);
      this.memberPortal.set(null);
      this.lookups.set({ [section.key]: rows });
      this.rows.set(rows);
      this.section.set(section);
      this.query.set('');
      this.page.set(1);
      this.connected.set(true);
      this.credentials.password = '';
    } catch (error) {
      this.authorization = '';
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  disconnect(): void {
    this.revision++;
    this.contributionJobRevision++;
    this.salaryCopyJobRevision++;
    this.batchHistoryRevision++;
    this.clearContributionJobPoll();
    this.clearSalaryCopyJobPoll();
    this.clearBatchHistoryPoll();
    this.batchHistory.set([]);
    this.batchHistoryLoading.set(false);
    this.batchHistoryError.set('');
    this.contributionJob.set(null);
    this.contributionJobStarting.set(false);
    this.contributionJobRefreshing.set(false);
    this.salaryCopyJob.set(null);
    this.salaryCopyJobStarting.set(false);
    this.salaryCopyJobRefreshing.set(false);
    this.authorization = '';
    this.processingOpen.set(false);
    this.contributionReportOpen.set(false);
    this.accountDetailsVisible.set(false);
    this.loginVisible.set(false);
    this.connected.set(false);
    this.session.set(null);
    this.memberPortal.set(null);
    this.rows.set([]);
    this.lookups.set({});
    this.claimBanks.set([]);
    this.claimBankInstitutionId = null;
    this.closeEditor();
    this.detail.set(null);
    this.closeRecordDetails();
    this.closeMemberDetails();
    this.pendingDelete.set(null);
    this.draft = {};
    this.claimLines = [];
    this.memberId = null;
    this.credentials.password = '';
    this.error.set('');
    this.notice.set('');
  }

  memberDependents(): RecordData[] {
    return this.memberPortal()?.member['dependents'] as RecordData[] ?? [];
  }

  memberPortalClaims(): RecordData[] { return this.memberPortal()?.claims ?? []; }
  memberPortalLetters(): RecordData[] { return this.memberPortal()?.guaranteeLetters ?? []; }

  selectMemberPortalTab(tab: 'member' | 'dependents' | 'claims' | 'letters'): void {
    this.memberPortalTab.set(tab);
    this.memberPortalPage.set(1);
  }

  setMemberPortalPage(page: number): void {
    this.memberPortalPage.set(Math.min(Math.max(1, page), this.memberPortalPageCount()));
  }

  memberPortalTabKey(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = ['member', 'dependents', 'claims', 'letters'] as const;
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (tabs.indexOf(this.memberPortalTab()) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[index];
    this.selectMemberPortalTab(tab);
    (event.currentTarget as HTMLElement).parentElement?.querySelector<HTMLButtonElement>(`#member-portal-nav-${tab}`)?.focus();
  }

  memberClaimBilledTotal(claim: RecordData): string {
    return this.money(this.lines(claim).reduce((sum, line) => sum + Number(line['billedAmount'] ?? 0), 0));
  }

  async refreshMemberPortal(): Promise<void> {
    if (this.session()?.role !== 'MEMBER' || this.busy()) return;
    this.busy.set(true);
    this.error.set('');
    try {
      this.memberPortal.set(await this.request<MemberPortalData>('GET', '/member/portal'));
      this.setMemberPortalPage(this.memberPortalPage());
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  userInitials(): string {
    const username = this.session()?.username.trim() ?? '';
    const parts = username.split(/[.\s_-]+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts.length > 1 ? parts.slice(0, 2).map(part => part[0]).join('') : username.slice(0, 2)).toUpperCase();
  }

  isReportSelected(key: string): boolean { return this.section().key === key; }

  async openReporting(): Promise<void> {
    this.processingOpen.set(false);
    this.contributionReportOpen.set(false);
    if (!this.reports().length) {
      this.openContributionReport();
      return;
    }
    if (this.reports().some(report => report.key === this.section().key)) return;
    await this.select('report-ledger');
  }

  openContributionReport(): void {
    if (!this.canViewContributionReport()) return;
    this.processingOpen.set(false);
    this.contributionReportOpen.set(true);
    this.processingAction.set('contribution-report');
    void this.loadContributionReport();
  }

  openProcessing(): void {
    const action = this.processingActions()[0];
    if (action) {
      this.processingAction.set(action.key);
      this.processingOpen.set(true);
      if (this.session()?.role === 'INSTITUTION' && this.session()?.institutionRole === 'ADMIN') {
        void this.loadBatchHistory();
      }
    }
  }

  async loadBatchHistory(): Promise<void> {
    if (this.batchHistoryLoading() || this.session()?.role !== 'INSTITUTION'
      || this.session()?.institutionRole !== 'ADMIN') return;
    const revision = ++this.batchHistoryRevision;
    this.clearBatchHistoryPoll();
    this.batchHistoryLoading.set(true);
    this.batchHistoryError.set('');
    try {
      const jobs = await this.request<RecordData[]>('GET', '/batch-jobs/today');
      if (revision !== this.batchHistoryRevision) return;
      this.batchHistory.set(jobs);
      this.scheduleBatchHistoryPoll(revision);
    } catch {
      if (revision === this.batchHistoryRevision) this.batchHistoryError.set('Unable to load today\'s batch history.');
    } finally {
      if (revision === this.batchHistoryRevision) this.batchHistoryLoading.set(false);
    }
  }

  batchHistoryTime(job: RecordData): string {
    return String(job['startedAt'] ?? '').replace('T', ' ').slice(0, 16);
  }

  private upsertBatchHistory(job: RecordData): void {
    const jobs = [job, ...this.batchHistory().filter(existing => existing['jobId'] !== job['jobId'])]
      .sort((left, right) => String(right['startedAt'] ?? '').localeCompare(String(left['startedAt'] ?? '')));
    this.batchHistory.set(jobs);
    this.scheduleBatchHistoryPoll(this.batchHistoryRevision);
  }

  private scheduleBatchHistoryPoll(revision: number): void {
    if (this.batchHistoryPollTimer !== null || revision !== this.batchHistoryRevision
      || !this.batchHistory().some(job => job['status'] === 'RUNNING')) return;
    this.batchHistoryPollTimer = setTimeout(() => {
      this.batchHistoryPollTimer = null;
      if (revision === this.batchHistoryRevision) void this.loadBatchHistory();
    }, 1500);
  }

  private clearBatchHistoryPoll(): void {
    if (this.batchHistoryPollTimer !== null) clearTimeout(this.batchHistoryPollTimer);
    this.batchHistoryPollTimer = null;
  }

  selectProcessingAction(key: string): void {
    if (!this.processingActions().some(action => action.key === key)) return;
    this.processingAction.set(key);
  }

  async loadContributionReport(force = false): Promise<void> {
    if (!this.canViewContributionReport() || this.contributionReportBusy()
      || (this.contributionReportLoaded() && !force)) return;
    this.contributionReportBusy.set(true);
    this.contributionReportError.set('');
    try {
      const rows = await this.request<RecordData[]>('GET',
        `/health-contributions/report?periodYear=${this.contributionReportYear()}&periodMonth=${this.contributionReportMonth()}`);
      this.contributionReportRows.set(rows);
      this.contributionReportLoaded.set(true);
    } catch {
      this.contributionReportError.set('Unable to load contribution report.');
    } finally {
      this.contributionReportBusy.set(false);
    }
  }

  async startContributionBatch(): Promise<void> {
    if (this.contributionJobStarting() || this.contributionJobRunning()
      || !this.processingActions().some(action => action.key === 'generate-contributions')) return;
    const revision = ++this.contributionJobRevision;
    this.clearContributionJobPoll();
    this.contributionJobStarting.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const job = await this.request<RecordData>('POST', '/health-contributions/generate', {
        periodYear: this.contributionPeriodYear(),
        periodMonth: this.contributionPeriodMonth(),
      });
      if (revision !== this.contributionJobRevision) return;
      this.contributionJob.set(job);
      this.upsertBatchHistory(job);
      this.notice.set('Contribution batch started.');
      if (job['status'] === 'RUNNING' && job['jobId']) {
        this.scheduleContributionJobPoll(String(job['jobId']), revision);
      } else {
        this.finishContributionJob(job);
      }
    } catch (error) {
      if (revision === this.contributionJobRevision) this.showError(error);
    } finally {
      if (revision === this.contributionJobRevision) this.contributionJobStarting.set(false);
    }
  }

  async startSalaryCopyBatch(): Promise<void> {
    if (this.salaryCopyJobStarting() || this.salaryCopyJobRunning() || this.salaryCopySamePeriod()
      || !this.processingActions().some(action => action.key === 'copy-salaries')) return;
    const revision = ++this.salaryCopyJobRevision;
    this.clearSalaryCopyJobPoll();
    this.salaryCopyJobStarting.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const job = await this.request<RecordData>('POST', '/monthly-salaries/copy', {
        sourcePeriodYear: this.salaryCopySourceYear(),
        sourcePeriodMonth: this.salaryCopySourceMonth(),
        targetPeriodYear: this.salaryCopyTargetYear(),
        targetPeriodMonth: this.salaryCopyTargetMonth(),
      });
      if (revision !== this.salaryCopyJobRevision) return;
      this.salaryCopyJob.set(job);
      this.upsertBatchHistory(job);
      this.notice.set('Salary copy batch started.');
      if (job['status'] === 'RUNNING' && job['jobId']) {
        this.scheduleSalaryCopyJobPoll(String(job['jobId']), revision);
      } else {
        this.finishSalaryCopyJob(job);
      }
    } catch (error) {
      if (revision === this.salaryCopyJobRevision) this.showError(error);
    } finally {
      if (revision === this.salaryCopyJobRevision) this.salaryCopyJobStarting.set(false);
    }
  }

  async refreshSalaryCopyJob(): Promise<void> {
    const jobId = this.salaryCopyJob()?.['jobId'];
    if (!jobId || this.salaryCopyJobRefreshing()) return;
    this.clearSalaryCopyJobPoll();
    await this.fetchSalaryCopyJob(String(jobId), this.salaryCopyJobRevision);
  }

  private scheduleSalaryCopyJobPoll(jobId: string, revision: number): void {
    this.salaryCopyJobPollTimer = setTimeout(() => {
      this.salaryCopyJobPollTimer = null;
      void this.fetchSalaryCopyJob(jobId, revision);
    }, 1000);
  }

  private async fetchSalaryCopyJob(jobId: string, revision: number): Promise<void> {
    if (revision !== this.salaryCopyJobRevision) return;
    this.salaryCopyJobRefreshing.set(true);
    try {
      const job = await this.request<RecordData>('GET', `/monthly-salaries/jobs/${encodeURIComponent(jobId)}`);
      if (revision !== this.salaryCopyJobRevision) return;
      this.salaryCopyJob.set(job);
      this.upsertBatchHistory(job);
      if (job['status'] === 'RUNNING') this.scheduleSalaryCopyJobPoll(jobId, revision);
      else this.finishSalaryCopyJob(job);
    } catch (error) {
      if (revision === this.salaryCopyJobRevision) this.showError(error);
    } finally {
      if (revision === this.salaryCopyJobRevision) this.salaryCopyJobRefreshing.set(false);
    }
  }

  private finishSalaryCopyJob(job: RecordData): void {
    if (job['status'] === 'DONE') {
      this.notice.set(String(job['resultSummary'] || 'Salaries copied.'));
    } else if (job['status'] === 'FAILED') {
      this.error.set(String(job['errorMessage'] || 'Salary copy batch failed.'));
    }
  }

  private clearSalaryCopyJobPoll(): void {
    if (this.salaryCopyJobPollTimer !== null) clearTimeout(this.salaryCopyJobPollTimer);
    this.salaryCopyJobPollTimer = null;
  }

  async refreshContributionJob(): Promise<void> {
    const jobId = this.contributionJob()?.['jobId'];
    if (!jobId || this.contributionJobRefreshing()) return;
    this.clearContributionJobPoll();
    await this.fetchContributionJob(String(jobId), this.contributionJobRevision);
  }

  private scheduleContributionJobPoll(jobId: string, revision: number): void {
    this.contributionJobPollTimer = setTimeout(() => {
      this.contributionJobPollTimer = null;
      void this.fetchContributionJob(jobId, revision);
    }, 1000);
  }

  private async fetchContributionJob(jobId: string, revision: number): Promise<void> {
    if (revision !== this.contributionJobRevision) return;
    this.contributionJobRefreshing.set(true);
    try {
      const job = await this.request<RecordData>('GET', `/health-contributions/jobs/${encodeURIComponent(jobId)}`);
      if (revision !== this.contributionJobRevision) return;
      this.contributionJob.set(job);
      this.upsertBatchHistory(job);
      if (job['status'] === 'RUNNING') this.scheduleContributionJobPoll(jobId, revision);
      else this.finishContributionJob(job);
    } catch (error) {
      if (revision === this.contributionJobRevision) this.showError(error);
    } finally {
      if (revision === this.contributionJobRevision) this.contributionJobRefreshing.set(false);
    }
  }

  private finishContributionJob(job: RecordData): void {
    if (job['status'] === 'DONE') {
      this.notice.set(String(job['resultSummary'] || 'Contributions generated.'));
    } else if (job['status'] === 'FAILED') {
      this.error.set(String(job['errorMessage'] || 'Contribution batch failed.'));
    }
  }

  private clearContributionJobPoll(): void {
    if (this.contributionJobPollTimer !== null) clearTimeout(this.contributionJobPollTimer);
    this.contributionJobPollTimer = null;
  }

  closeAccountMenu(event: Event): void {
    (event.currentTarget as HTMLDetailsElement).open = false;
    this.accountDetailsVisible.set(false);
  }

  async select(key: string): Promise<void> {
    const configuration = configurationSections.some(section => section.key === key);
    if (configuration && !this.canConfigure()) return;
    if (['SUPER_ADMIN', 'COMPANY'].includes(this.session()?.role ?? '') && !configuration && !this.sections().some(section => section.key === key)) return;
    if (this.busy() || (this.session()?.role === 'INSTITUTION' && !configuration
      && !this.sections().some(section => section.key === key) && !this.reports().some(section => section.key === key)
      && !['dependents', 'claims'].includes(key))) return;
    this.processingOpen.set(false);
    this.contributionReportOpen.set(false);
    this.section.set(this.sections().find(section => section.key === key) ?? allSections.find(section => section.key === key)!);
    this.query.set('');
    this.page.set(1);
    const startDate = reportSections.some(section => section.key === key) ? this.today() : '';
    this.reportStartDate.set(startDate);
    this.reportEndDate.set('');
    this.reportStartDateText.set(this.formatReportDate(startDate));
    this.reportEndDateText.set('');
    this.reportDateError.set('');
    this.closeEditor();
    this.detail.set(null);
    this.closeRecordDetails();
    this.closeMemberDetails();
    this.notice.set('');
    await this.refresh();
  }

  configurationTabKey(event: KeyboardEvent): void {
    if (this.busy() || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = configurationSections.findIndex(section => section.key === this.section().key);
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? configurationSections.length - 1
      : (current + (event.key === 'ArrowRight' ? 1 : -1) + configurationSections.length) % configurationSections.length;
    const section = configurationSections[index];
    void this.select(section.key);
    this.document.getElementById(`configuration-tab-${section.key}`)?.focus();
  }

  path(): string {
    const path = this.section().key === 'dependents' ? `/members/${this.memberId}/dependents` : this.section().path;
    if (!this.reportDateKey()) return path;
    const params = new URLSearchParams();
    if (this.reportStartDate()) params.set('startDate', this.reportStartDate());
    if (this.reportEndDate()) params.set('endDate', this.reportEndDate());
    const query = params.toString();
    return query ? `${path}?${query}` : path;
  }

  async refresh(): Promise<void> {
    const revision = ++this.revision;
    this.busy.set(true);
    this.error.set('');
    this.rows.set([]);
    try {
      if (this.section().key === 'dependents') {
        await this.loadLookup('members');
        if (!this.memberId) return;
      }
      const rows = await this.request<RecordData[]>('GET', this.path());
      if (revision === this.revision) {
        const visibleRows = this.section().key === 'drugstores' ? rows.filter(row => row['type'] === 'PHARMACY')
          : this.section().key === 'physicians' ? rows.filter(row => row['type'] !== 'PHARMACY') : rows;
        this.rows.set(visibleRows);
        this.page.set(Math.min(this.page(), this.pageCount()));
        this.lookups.update(values => ({ ...values, [this.section().key]: visibleRows }));
      }
    } catch (error) {
      if (revision === this.revision) this.showError(error);
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  async loadLookup(key: string): Promise<void> {
    const section = allSections.find(section => section.key === key);
    if (!section) return;
    const rows = await this.request<RecordData[]>('GET', section.path);
    this.lookups.update(values => ({ ...values, [key]: rows }));
  }

  canEdit(key = this.section().key): boolean {
    if (configurationSections.some(section => section.key === key)) return this.canConfigure();
    if (this.session()?.role === 'COMPANY') return ['members', 'companies'].includes(key);
    if (this.session()?.role === 'SUPER_ADMIN') return ['companies', 'providers', 'contracts'].includes(key);
    if (key === 'letters' || reportSections.some(section => section.key === key)) return false;
    if (this.session()?.role !== 'INSTITUTION') return true;
    if (['members', 'dependents', 'claims'].includes(key)) return true;
    return ['contracts', 'providers'].includes(key) && this.session()?.institutionRole === 'ADMIN';
  }

  canCreate(key = this.section().key): boolean {
    if (this.session()?.role === 'COMPANY') return key === 'members';
    if (this.session()?.role === 'SUPER_ADMIN') return this.canEdit(key);
    if (reportSections.some(section => section.key === key)) return false;
    if (key === 'letters') return true;
    return this.canEdit(key) && !(key === 'providers' && this.session()?.role === 'INSTITUTION');
  }

  canRemove(key = this.section().key): boolean {
    if (this.session()?.role === 'COMPANY') return key === 'members';
    return this.canEdit(key) && !(key === 'providers' && this.session()?.role === 'INSTITUTION');
  }

  canUpdateGuaranteeLetter(letter: RecordData): boolean {
    return letter['status'] === 'DRAFT' && ['OPERATOR', 'INSTITUTION', 'MEMBER'].includes(this.session()?.role ?? '');
  }

  canUpdateGuaranteeLines(letter: RecordData): boolean {
    return letter['status'] === 'ISSUED' && ['OPERATOR', 'INSTITUTION', 'MEMBER'].includes(this.session()?.role ?? '');
  }

  canValidateGuaranteeLetter(letter: RecordData): boolean {
    const session = this.session();
    return letter['status'] === 'ISSUED' && (session?.role === 'OPERATOR'
      || (session?.role === 'INSTITUTION' && session.institutionRole === 'ADMIN'));
  }

  canCancelValidatedGuaranteeLetter(letter: RecordData): boolean {
    return letter['status'] === 'VALIDATED' && ['OPERATOR', 'INSTITUTION'].includes(this.session()?.role ?? '');
  }

  canPayGuaranteeLetter(letter: RecordData): boolean {
    const session = this.session();
    return letter['status'] === 'VALIDATED' && (session?.role === 'OPERATOR'
      || (session?.role === 'INSTITUTION' && session.institutionRole === 'ADMIN'));
  }

  canCancelGuaranteeLetterPayment(letter: RecordData): boolean {
    const session = this.session();
    return letter['status'] === 'PAID' && (session?.role === 'OPERATOR'
      || (session?.role === 'INSTITUTION' && session.institutionRole === 'ADMIN'));
  }

  private defaultGuaranteeLetterPaymentReference(letter: RecordData, member: RecordData): string {
    const dependent = letter['dependentId'] == null ? undefined
      : this.dependents(member).find(item => String(item.id) === String(letter['dependentId']));
    return [
      member['socialSecurityNumber'], member['firstName'], member['lastName'],
      ...(dependent ? [dependent['socialSecurityNumber'], dependent['firstName'], dependent['lastName']] : []),
    ].map(value => String(value ?? '').trim()).filter(Boolean).join(' ');
  }

  async openGuaranteeLetterPayment(letter: RecordData, member: RecordData): Promise<void> {
    if (this.busy() || !letter.id || !member.id || !this.canPayGuaranteeLetter(letter)) return;
    const revision = this.memberDetailRevision;
    const letterId = Number(letter.id);
    const institutionId = Number(member['institutionId'] ?? this.session()?.institutionId) || null;
    const query = institutionId === null ? '' : `?institutionId=${institutionId}`;
    this.error.set('');
    this.guaranteeLetterPaymentMethod.set('');
    this.guaranteeLetterPaymentBankId.set(null);
    this.guaranteeLetterPaymentDeskId.set(null);
    this.guaranteeLetterPaymentReference.set(this.defaultGuaranteeLetterPaymentReference(letter, member));
    this.guaranteeLetterPaymentLookupsLoading.set(true);
    try {
      const [banks, desks] = await Promise.all([
        this.request<RecordData[]>('GET', `/referentiels/banks${query}`),
        this.request<RecordData[]>('GET', `/referentiels/payment-desks${query}`),
      ]);
      if (revision !== this.memberDetailRevision || this.letterDetail()?.letter.id !== letter.id) return;
      this.guaranteeLetterPaymentBanks.set(banks);
      this.guaranteeLetterPaymentDesks.set(desks);
      this.guaranteeLetterPaymentFormId.set(letterId);
    } catch (error) {
      if (revision === this.memberDetailRevision) this.showError(error);
    } finally {
      if (revision === this.memberDetailRevision) this.guaranteeLetterPaymentLookupsLoading.set(false);
    }
  }

  setGuaranteeLetterPaymentMethod(method: string): void {
    this.guaranteeLetterPaymentMethod.set(method);
    this.guaranteeLetterPaymentBankId.set(null);
    this.guaranteeLetterPaymentDeskId.set(null);
  }

  cancelGuaranteeLetterPaymentForm(): void {
    this.guaranteeLetterPaymentFormId.set(null);
    this.guaranteeLetterPaymentMethod.set('');
    this.guaranteeLetterPaymentBankId.set(null);
    this.guaranteeLetterPaymentDeskId.set(null);
    this.guaranteeLetterPaymentReference.set('');
    this.error.set('');
  }

  guaranteeLetterPaymentReady(): boolean {
    if (!this.guaranteeLetterPaymentReference().trim()) return false;
    if (this.guaranteeLetterPaymentMethod() === 'CASH') {
      return this.guaranteeLetterPaymentDesks().some(desk => desk.id === this.guaranteeLetterPaymentDeskId());
    }
    if (this.guaranteeLetterPaymentMethod() === 'BANK_TRANSFER') {
      return this.guaranteeLetterPaymentBanks().some(bank => bank.id === this.guaranteeLetterPaymentBankId());
    }
    return false;
  }

  async openEditor(row?: RecordData, context?: { section: Section; member: RecordData }): Promise<void> {
    const key = (context?.section ?? this.section()).key;
    if (!(row ? (key === 'letters' ? this.canUpdateGuaranteeLetter(row) : this.canEdit(key)) : this.canCreate(key))) return;
    this.editorContext.set(context ?? null);
    this.busy.set(true);
    this.error.set('');
    try {
      const section = this.editorSection();
      const sources = [...new Set(this.managementFields(section).map(field => field.source).filter((source): source is string =>
        !!source && source !== 'claimDependents' && !(this.session()?.role === 'MEMBER' && context && source === 'members')))];
      if (this.session()?.role === 'MEMBER' && context) {
        this.lookups.update(values => ({ ...values, members: [context.member], claimDependents: this.memberDependents().filter(dependent => dependent['active']) }));
      }
      if (['claims', 'letters'].includes(section.key)) sources.push('treatments', 'drugs');
      if (section.key === 'claims') sources.push('healthcare');
      await Promise.all(sources.map(source => this.loadLookup(source)));
      if (section.key === 'contracts') {
        const guarantees = await this.request<RecordData[]>('GET', '/contracts/guarantees');
        this.lookups.update(values => ({ ...values, guarantees }));
      }
      this.editingId.set(row?.id ?? null);
      this.draft = row ? { ...row, guaranteeIds: [...(row['guaranteeIds'] as number[] ?? [])] } : { active: true, relationshipType: 'CHILD', hasThirdPartyAgreement: false, affiliationDate: this.today(), treatmentDate: this.today(), startDate: this.today(), issueDate: this.today(), guaranteeIds: [] };
      if (section.key === 'members') this.draft['category'] ??= 'NON_CADRE';
      if (section.key === 'drugstores') this.draft['type'] = 'PHARMACY';
      if (section.key === 'physicians') this.draft['type'] ??= 'PHYSICIAN';
      if (section.key === 'letters') {
        const letterLines = row?.['lines'] as RecordData[] ?? [];
        const lineTypes = new Set(letterLines.map(line => line['drugCisCode'] ? 'PRESCRIPTION' : 'TREATMENT'));
        const inferredType = row?.['type'] ?? (lineTypes.size === 1 ? [...lineTypes][0] : lineTypes.size ? 'MIXED' : 'TREATMENT');
        this.draft['type'] = inferredType === 'MIXED' ? '' : inferredType;
        this.draft['healthcareProviderType'] = this.lookups()['healthcare']?.find(provider => provider.id === row?.['healthcareProviderId'])?.['type'] ?? null;
      }
      if (section.key === 'contracts') this.changeContractCompany();
      this.newGuarantees = [];
      if ((section.key === 'contracts' || configurationSections.some(item => item.key === section.key))
        && this.session()?.role === 'INSTITUTION') this.draft['institutionId'] = this.session()!.institutionId;
      this.lookups.update(values => ({ ...values, claimDependents: [] }));
      this.claimLines = [{ itemType: section.key === 'letters' && this.draft['type'] === 'PRESCRIPTION' ? 'drug' : 'treatment',
        paymentDestination: 'MEMBER', billedAmount: null }];
      if (section.key === 'letters' && row) {
        this.claimLines = (row['lines'] as RecordData[] ?? []).map(line => ({
          itemType: line['drugCisCode'] ? 'drug' : 'treatment',
          itemCode: line['drugCisCode'] ?? line['treatmentCode'] ?? null,
          guaranteedAmount: line['guaranteedAmount'] == null ? null : Number(line['guaranteedAmount']),
        }));
      }
      if (context && ['claims', 'letters'].includes(section.key)) {
        this.draft['memberId'] = context.member.id;
        this.draft['dependentId'] = null;
        if (this.session()?.role !== 'MEMBER') {
          const dependents = await this.request<RecordData[]>('GET', `/members/${context.member.id}/dependents`);
          this.lookups.update(values => ({ ...values, claimDependents: dependents.filter(dependent => dependent['active']) }));
        }
      }
      this.editor.set(true);
    } catch (error) {
      this.editorContext.set(null);
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async openMemberEditor(kind: 'members' | 'dependents' | 'claims' | 'letters', member: RecordData, row?: RecordData): Promise<void> {
    if (this.busy() || !member.id) return;
    if (kind === 'letters' && !row && member['active'] !== true) return;
    const section = sections.find(section => section.key === kind)!;
    await this.openEditor(row, { section, member });
  }

  async editGuaranteeLetter(member: RecordData, letter: RecordData): Promise<void> {
    if (this.busy() || !this.canUpdateGuaranteeLetter(letter)) return;
    this.letterDetail.set(null);
    await this.openMemberEditor('letters', member, letter);
    if (!this.editor()) this.letterDetail.set({ member, letter });
  }

  async editIssuedGuaranteeLines(context: { member: RecordData; letter: RecordData }): Promise<void> {
    if (this.busy() || !context.letter.id || !this.canUpdateGuaranteeLines(context.letter)) return;
    this.busy.set(true);
    this.error.set('');
    try {
      await Promise.all([this.loadLookup('treatments'), this.loadLookup('drugs')]);
      this.issuedGuaranteeLineDrafts = this.lines(context.letter).map(line => ({
        itemType: line['drugCisCode'] ? 'drug' : 'treatment',
        itemCode: line['drugCisCode'] ?? line['treatmentCode'] ?? null,
        guaranteedAmount: line['guaranteedAmount'] == null ? null : Number(line['guaranteedAmount']),
      }));
      this.issuedGuaranteeLinesEditing.set(Number(context.letter.id));
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  cancelIssuedGuaranteeLines(): void {
    this.issuedGuaranteeLinesEditing.set(null);
    this.issuedGuaranteeLineDrafts = [];
    this.error.set('');
  }

  addIssuedGuaranteeLine(letter: RecordData): void {
    if (this.issuedGuaranteeLineDrafts.length >= 100) return;
    this.issuedGuaranteeLineDrafts.push({
      itemType: letter['type'] === 'PRESCRIPTION' ? 'drug' : 'treatment', itemCode: null, guaranteedAmount: null,
    });
  }

  removeIssuedGuaranteeLine(index: number): void {
    if (this.issuedGuaranteeLineDrafts.length <= 1) return;
    this.issuedGuaranteeLineDrafts.splice(index, 1);
  }

  issuedGuaranteeLineTotal(): number {
    return this.issuedGuaranteeLineDrafts.reduce((sum, line) => sum + Number(line['guaranteedAmount'] ?? 0), 0);
  }

  async saveIssuedGuaranteeLines(context: { member: RecordData; letter: RecordData }): Promise<void> {
    const letterId = Number(context.letter.id);
    if (this.busy() || !letterId || !this.canUpdateGuaranteeLines(context.letter)
      || this.issuedGuaranteeLinesEditing() !== letterId) return;
    this.error.set('');
    this.busy.set(true);
    try {
      const lines = this.issuedGuaranteeLineDrafts.map(line => ({
        treatmentCode: line['itemType'] === 'treatment' ? line['itemCode'] : null,
        drugCisCode: line['itemType'] === 'drug' ? line['itemCode'] : null,
        guaranteedAmount: Number(line['guaranteedAmount']),
      }));
      const updated = await this.request<RecordData>('PUT',
        `/members/${context.member.id}/guarantee-letters/${letterId}/lines`, { lines });
      this.memberLetters.update(rows => rows.map(letter => letter.id === updated.id ? updated : letter));
      this.memberPortal.update(portal => portal ? {
        ...portal, guaranteeLetters: portal.guaranteeLetters.map(letter => letter.id === updated.id ? updated : letter),
      } : portal);
      this.letterDetail.set({ member: context.member, letter: updated });
      this.issuedGuaranteeLinesEditing.set(null);
      this.issuedGuaranteeLineDrafts = [];
      this.notice.set('Changes saved.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async validateGuaranteeLetter(context: { member: RecordData; letter: RecordData }): Promise<void> {
    const letterId = Number(context.letter.id);
    if (this.busy() || !letterId || !this.canValidateGuaranteeLetter(context.letter)) return;
    this.error.set('');
    this.busy.set(true);
    try {
      const validated = await this.request<RecordData>('POST',
        `/members/${context.member.id}/guarantee-letters/${letterId}/validate`);
      this.memberLetters.update(rows => rows.map(letter => letter.id === validated.id ? validated : letter));
      this.memberPortal.update(portal => portal ? {
        ...portal, guaranteeLetters: portal.guaranteeLetters.map(letter => letter.id === validated.id ? validated : letter),
      } : portal);
      this.letterDetail.set({ member: context.member, letter: validated });
      this.notice.set('Guarantee letter validated.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async payGuaranteeLetter(letter: RecordData, member: RecordData): Promise<void> {
    if (!letter.id || !member.id || this.memberLettersBusy() || !this.canPayGuaranteeLetter(letter)
      || this.guaranteeLetterPaymentFormId() !== Number(letter.id) || !this.guaranteeLetterPaymentReady()) return;
    const revision = this.memberDetailRevision;
    this.memberLettersBusy.set(true);
    this.error.set('');
    try {
      const paymentMethod = this.guaranteeLetterPaymentMethod();
      const paid = await this.request<RecordData>('POST', `/members/${member.id}/guarantee-letters/${letter.id}/pay`, {
        paymentMethod,
        paymentReference: this.guaranteeLetterPaymentReference().trim(),
        paymentBankId: paymentMethod === 'BANK_TRANSFER' ? this.guaranteeLetterPaymentBankId() : null,
        paymentDeskId: paymentMethod === 'CASH' ? this.guaranteeLetterPaymentDeskId() : null,
      });
      if (revision !== this.memberDetailRevision) return;
      this.memberLetters.update(rows => rows.map(item => item.id === paid.id ? paid : item));
      this.memberPortal.update(portal => portal ? {
        ...portal, guaranteeLetters: portal.guaranteeLetters.map(item => item.id === paid.id ? paid : item),
      } : portal);
      if (this.letterDetail()?.letter.id === paid.id) this.letterDetail.set({ member, letter: paid });
      this.cancelGuaranteeLetterPaymentForm();
      this.notice.set('Guarantee letter marked as paid.');
    } catch {
      if (revision === this.memberDetailRevision) this.error.set('Unable to pay guarantee letter.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberLettersBusy.set(false);
    }
  }

  async cancelGuaranteeLetterPayment(letter: RecordData, member: RecordData): Promise<void> {
    if (!letter.id || !member.id || this.memberLettersBusy() || !this.canCancelGuaranteeLetterPayment(letter)) return;
    const revision = this.memberDetailRevision;
    this.memberLettersBusy.set(true);
    this.error.set('');
    try {
      const validated = await this.request<RecordData>('POST',
        `/members/${member.id}/guarantee-letters/${letter.id}/cancel-payment`);
      if (revision !== this.memberDetailRevision) return;
      this.memberLetters.update(rows => rows.map(item => item.id === validated.id ? validated : item));
      this.memberPortal.update(portal => portal ? {
        ...portal, guaranteeLetters: portal.guaranteeLetters.map(item => item.id === validated.id ? validated : item),
      } : portal);
      if (this.letterDetail()?.letter.id === validated.id) this.letterDetail.set({ member, letter: validated });
      this.notice.set('Guarantee-letter payment cancelled.');
    } catch {
      if (revision === this.memberDetailRevision) this.error.set('Unable to cancel guarantee-letter payment.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberLettersBusy.set(false);
    }
  }

  async editDependent(member: RecordData, dependent: RecordData): Promise<void> {
    if (this.busy()) return;
    await this.openMemberEditor('dependents', member, dependent);
    if (this.editor()) this.closeDependentDetails();
  }

  openDependentDetails(member: RecordData, dependent: RecordData): void {
    this.dependentDetailsExpanded.set(false);
    this.dependentRecordTab.set('claims');
    this.dependentDetail.set({ member, dependent });
    if (!this.session() || this.session()?.role === 'COMPANY' || this.session()?.role === 'MEMBER'
      || this.memberDetail()?.id !== member.id) return;
    if (!this.memberClaimsLoaded() && !this.memberClaimsBusy()) void this.loadMemberClaims();
    if (!this.memberLettersLoaded() && !this.memberLettersBusy()) void this.loadMemberLetters();
  }

  closeDependentDetails(): void {
    this.dependentDetailsExpanded.set(false);
    this.dependentRecordTab.set('claims');
    this.dependentDetail.set(null);
  }

  closeEditor(): void {
    this.editor.set(false);
    this.editorContext.set(null);
    this.guaranteeDetail.set(null);
    this.pendingGuaranteeDelete.set(null);
    this.newGuarantees = [];
    this.error.set('');
  }

  companyGuarantees(company: RecordData): RecordData[] {
    return company['guarantees'] as RecordData[] ?? [];
  }

  guaranteeCaps(guarantee: RecordData): RecordData[] {
    return guarantee['caps'] as RecordData[] ?? [];
  }

  async openGuaranteeDetails(company: RecordData, guarantee: RecordData): Promise<void> {
    if (this.busy() || !company.id || !guarantee.id) return;
    this.error.set('');
    this.busy.set(true);
    try {
      const details = await this.request<RecordData>('GET', `/companies/${company.id}/guarantees/${guarantee.id}`);
      this.guaranteeDetail.set({ company, guarantee: details });
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  requestGuaranteeDelete(company: RecordData, guarantee: RecordData): void {
    if (this.busy() || !company.id || !guarantee.id || !this.canEdit('companies')) return;
    this.error.set('');
    this.notice.set('');
    this.pendingGuaranteeDelete.set({ company, guarantee });
  }

  cancelGuaranteeDelete(): void {
    if (this.busy()) return;
    this.pendingGuaranteeDelete.set(null);
    this.error.set('');
  }

  async deleteCompanyGuarantee(): Promise<void> {
    const pending = this.pendingGuaranteeDelete();
    if (this.busy() || !pending || !this.canEdit('companies')) return;
    this.busy.set(true);
    this.error.set('');
    try {
      await this.request('DELETE', `/companies/${pending.company.id}/guarantees/${pending.guarantee.id}`);
      const withoutGuarantee = (company: RecordData): RecordData => ({ ...company,
        guarantees: this.companyGuarantees(company).filter(guarantee => guarantee.id !== pending.guarantee.id) });
      this.rows.update(rows => rows.map(company => company.id === pending.company.id ? withoutGuarantee(company) : company));
      this.lookups.update(values => ({ ...values,
        companies: (values['companies'] ?? []).map(company => company.id === pending.company.id ? withoutGuarantee(company) : company),
        guarantees: (values['guarantees'] ?? []).filter(guarantee => !(guarantee.id === pending.guarantee.id
          && guarantee['companySiret'] === pending.company['siretNumber'])) }));
      const detail = this.recordDetail();
      if (detail?.section.key === 'companies' && detail.row.id === pending.company.id) {
        this.recordDetail.set({ ...detail, row: withoutGuarantee(detail.row) });
      }
      if (this.editorSection().key === 'companies' && this.editingId() === pending.company.id) {
        this.draft = withoutGuarantee(this.draft);
      }
      this.guaranteeDetail.set(null);
      this.pendingGuaranteeDelete.set(null);
      this.notice.set('Guarantee package deleted.');
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.error.set('Guarantee packages used by contracts cannot be deleted.');
      } else this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  addCompanyGuarantee(): void {
    if (this.busy() || this.newGuarantees.length >= 20) return;
    this.newGuarantees.push({ name: '', category: 'NON_CADRE', riskType: 'HEALTH', reimbursementRate: 20,
      socialSecurityReimbursementRate: 70, memberReimbursementRate: 10, memberTaxRate: 0, companyTaxRate: 0,
      capType: 'ANNUAL_BUDGET', maxAmount: null, maxQuantity: null });
  }

  async changeClaimMember(): Promise<void> {
    this.draft['dependentId'] = null;
    this.lookups.update(values => ({ ...values, claimDependents: [] }));
    if (!this.draft['memberId']) return;
    this.busy.set(true);
    try {
      const dependents = await this.request<RecordData[]>('GET', `/members/${this.draft['memberId']}/dependents`);
      this.lookups.update(values => ({ ...values, claimDependents: dependents.filter(row => row['active']) }));
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  options(field: Field): RecordData[] {
    if (field.key === 'healthcareProviderType' && this.editorSection().key === 'letters') {
      const type = this.draft['type'];
      if (type !== 'TREATMENT' && type !== 'PRESCRIPTION') return [];
      return (field.options ?? []).filter(providerType =>
        type === 'PRESCRIPTION' ? providerType === 'PHARMACY' : providerType !== 'PHARMACY')
        .map(value => ({ value, label: this.i18n.text(value) }));
    }
    if (field.key === 'healthcareProviderId' && this.editorSection().key === 'letters') {
      const type = this.draft['type'];
      if (type !== 'TREATMENT' && type !== 'PRESCRIPTION') return [];
      const providerType = this.draft['healthcareProviderType'];
      if (!providerType) return [];
      return (this.lookups()['healthcare'] ?? []).filter(provider =>
        provider['type'] === providerType && (type === 'PRESCRIPTION'
          ? provider['type'] === 'PHARMACY' : provider['type'] !== 'PHARMACY'));
    }
    return field.source ? this.lookups()[field.source] ?? [] : (field.options ?? []).map(value => ({ value, label: this.i18n.text(value) }));
  }

  optionValue(field: Field, option: RecordData): unknown {
    return field.source === 'companies' ? option['siretNumber'] : option.id ?? option['value'];
  }

  private managementFields(section: Section): Field[] {
    return section.fields.filter(field => !(configurationSections.some(item => item.key === section.key)
      && this.session()?.role === 'INSTITUTION' && field.key === 'institutionId'))
      .map(field => section.key === 'contracts' && field.key === 'companySiret' && this.session()?.role === 'SUPER_ADMIN'
      ? { ...field, label: 'Company', type: 'select', source: 'companies' } : field);
  }

  private fieldGroups(section: Section) {
    const fields = this.managementFields(section);
    const address = fields.filter(field => field.group === 'address');
    return [
      { label: '', fields: fields.filter(field => field.group !== 'address') },
      ...(address.length ? [{ label: 'Address', fields: address }] : []),
    ];
  }

  openRecordDetails(row: RecordData): void {
    if (this.busy()) return;
    this.closeRecordDetails();
    this.error.set('');
    this.notice.set('');
    this.recordDetail.set({ section: this.section(), row });
    if (this.section().key === 'companies') void this.loadCompanyMembers();
  }

  closeRecordDetails(): void {
    this.companyMemberRevision++;
    this.companySalaryRevision++;
    this.companyTab.set('guarantees');
    this.recordDetail.set(null);
    this.contractGuaranteeDetail.set(null);
    this.guaranteeDetail.set(null);
    this.pendingGuaranteeDelete.set(null);
    this.companyMembers.set([]);
    this.companyMembersBusy.set(false);
    this.companyMembersError.set('');
    this.companyMembersPage.set(1);
    this.companySalaries.set([]);
    this.companySalariesBusy.set(false);
    this.companySalariesLoaded.set(false);
    this.companySalariesError.set('');
    this.companySalarySaving.set(false);
    this.companySalaryError.set('');
    this.companySalaryEditing.set(null);
    this.companySalaryPage.set(1);
    this.resetCompanySalaryDraft();
  }

  async loadCompanyMembers(): Promise<void> {
    const detail = this.recordDetail();
    if (detail?.section.key !== 'companies' || !detail.row.id) return;
    const revision = ++this.companyMemberRevision;
    this.companyMembersBusy.set(true);
    this.companyMembersError.set('');
    try {
      const members = await this.request<RecordData[]>('GET', `/companies/${detail.row.id}/members`);
      if (revision === this.companyMemberRevision) {
        this.companyMembers.set(members);
        this.setCompanyMembersPage(this.companyMembersPage());
      }
    } catch {
      if (revision === this.companyMemberRevision) this.companyMembersError.set('Unable to load company members.');
    } finally {
      if (revision === this.companyMemberRevision) this.companyMembersBusy.set(false);
    }
  }

  setCompanyMembersPage(page: number): void {
    this.companyMembersPage.set(Math.min(Math.max(1, page), this.companyMembersPageCount()));
  }

  async selectCompanyTab(tab: 'guarantees' | 'members' | 'salaries'): Promise<void> {
    this.companyTab.set(tab);
    if (tab === 'salaries' && !this.companySalariesLoaded()) await this.loadCompanySalaries();
  }

  async loadCompanySalaries(force = false): Promise<void> {
    const detail = this.recordDetail();
    if (detail?.section.key !== 'companies' || !detail.row.id || this.companySalariesBusy()
      || (this.companySalariesLoaded() && !force)) return;
    const revision = ++this.companySalaryRevision;
    this.companySalariesBusy.set(true);
    this.companySalariesError.set('');
    try {
      const salaries = await this.request<RecordData[]>('GET', `/companies/${detail.row.id}/monthly-salaries`);
      if (revision === this.companySalaryRevision) {
        this.companySalaries.set(salaries);
        this.companySalaryPage.set(1);
        this.companySalariesLoaded.set(true);
      }
    } catch {
      if (revision === this.companySalaryRevision) this.companySalariesError.set('Unable to load monthly salaries.');
    } finally {
      if (revision === this.companySalaryRevision) this.companySalariesBusy.set(false);
    }
  }

  setCompanySalaryPage(page: number): void {
    this.companySalaryPage.set(Math.min(Math.max(1, page), this.companySalaryPageCount()));
  }

  editCompanySalary(salary: RecordData): void {
    if (!salary.id || this.companySalarySaving()) return;
    this.companySalaryEditing.set(salary.id);
    this.companySalaryError.set('');
    this.companySalaryDraft.memberId = Number(salary['memberId']);
    this.companySalaryDraft.periodYear = Number(salary['periodYear']);
    this.companySalaryDraft.periodMonth = Number(salary['periodMonth']);
    this.companySalaryDraft.grossSalary = Number(salary['grossSalary']);
  }

  cancelCompanySalaryEdit(): void {
    if (this.companySalarySaving()) return;
    this.companySalaryError.set('');
    this.resetCompanySalaryDraft();
  }

  async saveCompanySalary(): Promise<void> {
    const detail = this.recordDetail();
    if (detail?.section.key !== 'companies' || !detail.row.id || this.companySalarySaving()) return;
    const revision = this.companySalaryRevision;
    const editingId = this.companySalaryEditing();
    this.companySalarySaving.set(true);
    this.companySalaryError.set('');
    try {
      const path = `/companies/${detail.row.id}/monthly-salaries${editingId === null ? '' : `/${editingId}`}`;
      const salary = await this.request<RecordData>(editingId === null ? 'POST' : 'PUT', path, {
        ...this.companySalaryDraft,
      });
      if (revision === this.companySalaryRevision && this.recordDetail()?.row.id === detail.row.id) {
        this.companySalaries.update(salaries => this.sortSalaries(editingId === null
          ? [...salaries, salary] : salaries.map(existing => existing.id === salary.id ? salary : existing)));
        this.companySalaryPage.set(1);
        this.companySalariesLoaded.set(true);
        this.resetCompanySalaryDraft();
      }
    } catch (error) {
      if (revision === this.companySalaryRevision) {
        this.companySalaryError.set(error instanceof HttpErrorResponse && error.status === 409
          ? 'Monthly salary already exists for this member and period.'
          : editingId === null ? 'Unable to add monthly salary.' : 'Unable to update monthly salary.');
      }
    } finally {
      if (revision === this.companySalaryRevision) this.companySalarySaving.set(false);
    }
  }

  salaryPeriod(salary: RecordData): string {
    const year = Number(salary['periodYear']);
    const month = Number(salary['periodMonth']);
    return new Intl.DateTimeFormat(this.i18n.locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' })
      .format(new Date(Date.UTC(year, month - 1, 1)));
  }

  private resetCompanySalaryDraft(): void {
    this.companySalaryEditing.set(null);
    this.companySalaryDraft.memberId = null;
    this.companySalaryDraft.periodYear = new Date().getFullYear();
    this.companySalaryDraft.periodMonth = new Date().getMonth() + 1;
    this.companySalaryDraft.grossSalary = null;
  }

  private sortSalaries(salaries: RecordData[]): RecordData[] {
    return [...salaries].sort((left, right) => Number(right['periodYear']) - Number(left['periodYear'])
      || Number(right['periodMonth']) - Number(left['periodMonth']) || Number(right.id ?? 0) - Number(left.id ?? 0));
  }

  companyTabKey(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = ['guarantees', 'members', 'salaries'] as const;
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (tabs.indexOf(this.companyTab()) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[index];
    void this.selectCompanyTab(tab);
    (event.currentTarget as HTMLElement).parentElement?.querySelector<HTMLButtonElement>(`#company-tab-${tab}`)?.focus();
  }

  recordDetailValue(row: RecordData, field: Field): string {
    if (field.key === 'institutionId' && row['institutionName']) return String(row['institutionName']);
    if (field.source && row[field.key] != null) {
      const related = this.lookups()[field.source]?.find(option => this.optionValue(field, option) === row[field.key]);
      if (related) return this.label(related);
    }
    return this.cell(row, field.key);
  }

  addressLines(member: RecordData): string[] {
    const value = (key: string) => String(member[key] ?? '').trim();
    const street = [value('houseNumber'), value('street')].filter(Boolean).join(' ');
    return [
      [street, value('addressComplement')].filter(Boolean).join(', '),
      [value('postalCode'), value('city')].filter(Boolean).join(' '),
      value('country'),
    ].filter(Boolean);
  }

  coverageDetails(row: RecordData): string {
    const ids = row['guaranteeIds'] as number[] | undefined;
    return ids?.map(id => {
      const guarantee = this.lookups()['guarantees']?.find(guarantee => guarantee.id === id);
      return guarantee ? this.label(guarantee) : `#${id}`;
    }).join(', ') || '-';
  }

  recordGuarantees(row: RecordData): RecordData[] {
    const guarantees = row['guarantees'] as RecordData[] | undefined;
    if (guarantees) return guarantees;
    const ids = row['guaranteeIds'] as number[] | undefined;
    return ids?.map(id => this.lookups()['guarantees']?.find(guarantee => guarantee.id === id) ?? { id, name: `#${id}` }) ?? [];
  }

  openContractGuaranteeDetails(guarantee: RecordData): void {
    this.contractGuaranteeDetail.set(guarantee);
  }

  label(row: RecordData): string {
    if (row['legalName']) return String(row['legalName']) + (row['siretNumber'] ? ` (${row['siretNumber']})` : '');
    return String(row['corporateName'] ?? row['contractNumber'] ?? row['description'] ?? row['label'] ?? row['name'] ?? `${row['firstName'] ?? ''} ${row['lastName'] ?? ''}`.trim()) + (row.id ? ` #${row.id}` : '');
  }

  async save(): Promise<void> {
    const section = this.editorSection();
    const editingLetter = section.key === 'letters' && this.editingId() !== null;
    if (section.key === 'letters' && !['TREATMENT', 'PRESCRIPTION'].includes(String(this.draft['type'] ?? ''))) return;
    if (editingLetter ? !this.canUpdateGuaranteeLetter(this.draft) : !(this.editingId() ? this.canEdit(section.key) : this.canCreate(section.key))) return;
    this.error.set('');
    this.busy.set(true);
    try {
      const context = this.editorContext();
      const section = this.editorSection();
      let payload: RecordData = Object.fromEntries(section.fields.map(field => [field.key, this.draft[field.key] === '' || this.draft[field.key] === undefined ? null : this.draft[field.key]]));
      if (configurationSections.some(item => item.key === section.key) && section.fields.some(field => field.key === 'institutionId')
        && this.session()?.role === 'INSTITUTION') {
        payload['institutionId'] = this.session()!.institutionId;
      }
      if (section.key === 'companies' && this.newGuarantees.length) {
        payload['newGuarantees'] = this.newGuarantees.map(guarantee => ({ name: guarantee.name.trim(),
          category: guarantee.category,
          riskType: guarantee.riskType, reimbursementRate: guarantee.reimbursementRate,
          socialSecurityReimbursementRate: guarantee.socialSecurityReimbursementRate,
          memberReimbursementRate: guarantee.memberReimbursementRate,
          memberTaxRate: guarantee.memberTaxRate, companyTaxRate: guarantee.companyTaxRate,
          caps: [{ capType: guarantee.capType,
            maxAmount: guarantee.capType === 'QUANTITY_PER_YEAR' ? null : guarantee.maxAmount,
            maxQuantity: guarantee.capType === 'QUANTITY_PER_YEAR' ? guarantee.maxQuantity : null }] }));
      }
      if (section.key === 'contracts') {
        this.changeContractCompany();
        payload['guaranteeIds'] = this.draft['guaranteeIds'];
        if (this.session()?.role === 'INSTITUTION') payload['institutionId'] = this.session()!.institutionId;
      }
      if (section.key === 'claims') {
        const member = this.lookups()['members']?.find(row => row.id === Number(this.draft['memberId']));
        if (!member) throw new Error('Select a member.');
        payload = { socialSecurityNumber: member['socialSecurityNumber'], treatmentDate: this.draft['treatmentDate'], practitionerId: this.draft['practitionerId'] || null, dependentId: this.draft['dependentId'] || null,
          lines: this.claimLines.map(line => ({ treatmentCode: line['itemType'] === 'treatment' ? line['itemCode'] : null, drugCisCode: line['itemType'] === 'drug' ? line['itemCode'] : null, providerRppsCode: line['providerRppsCode'] || null, paymentDestination: line['paymentDestination'], billedAmount: line['billedAmount'] })) };
      }
      if (section.key === 'letters') {
        delete payload['healthcareProviderType'];
        delete payload['memberId'];
        if (this.draft['validUntil'] && String(this.draft['validUntil']) < String(this.draft['issueDate'])) {
          throw new Error('The expiry date must not precede the issue date');
        }
        if (!this.claimLines.length) throw new Error('Add at least one guarantee line.');
        payload['lines'] = this.claimLines.map(line => {
          const amount = Number(line['guaranteedAmount']);
          if (!line['itemCode'] || !Number.isFinite(amount) || amount < 0.01) {
            throw new Error('Select an item and a positive amount for every guarantee line.');
          }
          return { treatmentCode: line['itemType'] === 'treatment' ? line['itemCode'] : null,
            drugCisCode: line['itemType'] === 'drug' ? line['itemCode'] : null, guaranteedAmount: amount };
        });
      }
      const id = this.editingId();
      const path = context ? ['dependents', 'letters'].includes(section.key)
        ? `/members/${context.member.id}/${section.key === 'letters' ? 'guarantee-letters' : 'dependents'}` : section.path : this.path();
      const saved = await this.request<RecordData>(id ? 'PUT' : 'POST', path + (id ? `/${id}` : ''), payload);
      this.closeEditor();
      const recordDetail = this.recordDetail();
      if (recordDetail?.section.key === section.key && recordDetail.row.id === saved.id) {
        this.recordDetail.set({ ...recordDetail, row: saved });
      }
      if (context && this.session()?.role === 'MEMBER' && ['dependents', 'claims', 'letters'].includes(section.key)) {
        const portal = await this.request<MemberPortalData>('GET', '/member/portal');
        this.memberPortal.set(portal);
        this.lookups.set({ members: [portal.member] });
        if (id && section.key === 'letters') {
          const letter = portal.guaranteeLetters.find(item => item.id === saved.id) ?? saved;
          this.letterDetail.set({ member: portal.member, letter });
        }
      } else if (context && section.key === 'members') {
        this.memberDetail.set(saved);
        this.rows.update(rows => rows.map(row => row.id === saved.id ? saved : row));
        this.lookups.update(values => ({ ...values, members: (values['members'] ?? []).map(row => row.id === saved.id ? saved : row) }));
      } else if (context && section.key === 'dependents') {
        const dependents = this.dependents(context.member);
        const member = { ...context.member, dependents: id ? dependents.map(dependent => dependent.id === id ? saved : dependent) : [...dependents, saved] };
        this.memberDetail.set(member);
        this.rows.update(rows => rows.map(row => row.id === member.id ? member : row));
        this.lookups.update(values => ({ ...values, members: (values['members'] ?? []).map(row => row.id === member.id ? member : row) }));
      } else if (context && section.key === 'letters') {
        this.memberLettersLoaded.set(false);
        await this.loadMemberLetters();
        if (id) {
          const letter = this.memberLetters().find(item => item.id === saved.id) ?? saved;
          this.letterDetail.set({ member: context.member, letter });
        }
      } else if (context) {
        this.memberClaimsLoaded.set(false);
        await this.loadMemberClaims();
      } else {
        await this.refresh();
      }
      this.notice.set(id ? 'Changes saved.' : 'Record created.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async remove(): Promise<void> {
    if (!this.canRemove()) return;
    const row = this.pendingDelete();
    if (!row) return;
    this.busy.set(true);
    try {
      await this.request('DELETE', `${this.path()}/${row.id}`);
      this.pendingDelete.set(null);
      await this.refresh();
      this.notice.set(['members', 'dependents'].includes(this.section().key) ? 'Record deactivated.' : 'Record deleted.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async process(row: RecordData): Promise<void> {
    if (this.busy() || !row.id || row['status'] !== 'PENDING') return;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const result = await this.request<RecordData>('POST', `/health-claims/${row.id}/process`);
      if (this.section().key === 'claims') this.rows.update(rows => rows.map(claim => claim.id === result.id ? result : claim));
      this.memberClaims.update(claims => claims.map(claim => claim.id === result.id ? result : claim));
      this.detail.set(result);
      this.notice.set('Claim processed.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  async rejectClaim(row: RecordData): Promise<void> {
    if (this.busy() || !row.id || row['status'] !== 'PENDING') return;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const result = await this.request<RecordData>('POST', `/health-claims/${row.id}/reject`);
      if (this.section().key === 'claims') this.rows.update(rows => rows.map(claim => claim.id === result.id ? result : claim));
      this.memberClaims.update(claims => claims.map(claim => claim.id === result.id ? result : claim));
      this.detail.set(result);
      this.notice.set('Claim rejected.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  openClaimDetails(row: RecordData): void {
    this.detail.set(row);
    this.setClaimPaymentMethod('');
    this.claimPaymentReference.set(String(row['defaultPaymentReference'] ?? ''));
  }

  setClaimPaymentMethod(method: string): void {
    this.claimPaymentMethod.set(method);
    this.claimPaymentDesk.set('');
    this.claimBankName.set('');
    this.claimBankBic.set('');
    this.claimBanksError.set('');
    this.claimBankAccountNumber.set(method === 'BANK_TRANSFER'
      ? String(this.detail()?.['defaultBankAccountNumber'] ?? '') : '');
    this.claimMobileNumber.set(method === 'MOBILE_WALLET'
      ? String(this.detail()?.['defaultMobileNumber'] ?? '') : '');
    if (method === 'BANK_TRANSFER') void this.loadClaimBanks();
  }

  selectClaimBank(bic: string): void {
    const bank = this.claimBanks().find(bank => bank['bic'] === bic);
    this.claimBankBic.set(bank ? bic : '');
    this.claimBankName.set(String(bank?.['name'] ?? ''));
  }

  async loadClaimBanks(): Promise<void> {
    if (this.claimBanksLoading()) return;
    const authorization = this.authorization;
    const institutionId = this.claimInstitutionId();
    this.claimBanksError.set('');
    this.claimBanksLoading.set(true);
    try {
      if (!this.claimBanks().length || this.claimBankInstitutionId !== institutionId) {
        this.claimBanks.set([]);
        const query = institutionId === null ? '' : `?institutionId=${institutionId}`;
        const banks = await this.request<RecordData[]>('GET', '/referentiels/banks' + query);
        if (authorization !== this.authorization || institutionId !== this.claimInstitutionId()) return;
        this.claimBanks.set(banks);
        this.claimBankInstitutionId = institutionId;
      }
      if (this.claimPaymentMethod() === 'BANK_TRANSFER' && !this.claimBankBic()) {
        const bic = String(this.detail()?.['defaultBankAccountBic'] ?? '').trim().toUpperCase();
        const banks = this.claimBanks();
        const bank = banks.find(bank => bank['bic'] === bic)
          ?? banks.find(bank => String(bank['bic']) + 'XXX' === bic || bank['bic'] === bic + 'XXX');
        this.selectClaimBank(String(bank?.['bic'] ?? ''));
      }
    } catch {
      if (authorization === this.authorization) this.claimBanksError.set('Unable to load banks.');
    } finally {
      this.claimBanksLoading.set(false);
      if (authorization === this.authorization && this.detail() && this.claimPaymentMethod() === 'BANK_TRANSFER'
        && institutionId !== this.claimInstitutionId()) void this.loadClaimBanks();
    }
  }

  private claimInstitutionId(): number | null {
    return Number(this.detail()?.['institutionId'] ?? this.session()?.institutionId) || null;
  }

  claimPaymentReady(): boolean {
    switch (this.claimPaymentMethod()) {
      case 'CASH': return !!this.claimPaymentDesk().trim();
      case 'BANK_TRANSFER': return !this.claimBanksLoading() && !!this.claimBankAccountNumber().trim()
        && !!this.claimBanks().some(bank => bank['bic'] === this.claimBankBic()
          && bank['name'] === this.claimBankName());
      case 'MOBILE_WALLET': return !!this.claimMobileNumber().trim();
      default: return false;
    }
  }

  async recordClaimPayment(row: RecordData): Promise<void> {
    const paymentMethod = this.claimPaymentMethod();
    if (this.busy() || !row.id || row['status'] !== 'PROCESSED' || !this.claimPaymentReady()) return;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const paymentReference = this.claimPaymentReference().trim();
      const result = await this.request<RecordData>('POST', `/health-claims/${row.id}/pay`, {
        paymentMethod,
        paymentDesk: paymentMethod === 'CASH' ? this.claimPaymentDesk().trim() : null,
        bankName: paymentMethod === 'BANK_TRANSFER' ? this.claimBankName().trim() : null,
        bankAccountNumber: paymentMethod === 'BANK_TRANSFER' ? this.claimBankAccountNumber().trim() : null,
        mobileNumber: paymentMethod === 'MOBILE_WALLET' ? this.claimMobileNumber().trim() : null,
        paymentReference: paymentReference || null,
      });
      if (this.section().key === 'claims') this.rows.update(rows => rows.map(claim => claim.id === result.id ? result : claim));
      this.memberClaims.update(claims => claims.map(claim => claim.id === result.id ? result : claim));
      this.detail.set(result);
      this.notice.set('Claim payment recorded.');
    } catch (error) {
      this.showError(error);
    } finally {
      this.busy.set(false);
    }
  }

  cell(row: RecordData, key: string): string {
    if (key === 'category') return this.i18n.text(String(row[key] ?? 'NON_CADRE'));
    if (key === 'name' && row['firstName']) return `${row['firstName']} ${row['lastName']}`;
    if (key === 'billedTotal') return this.money(this.lines(row).reduce((sum, line) => sum + Number(line['billedAmount'] ?? 0), 0));
    const value = row[key];
    if (value === null || value === undefined || value === '') return '-';
    if (key === 'active') return this.i18n.text(value ? 'Active' : 'Inactive');
    if (typeof value === 'boolean') return this.i18n.text(value ? 'Yes' : 'No');
    if (key === 'socialSecurityBasePrice') return this.money(Number(value));
    if (['affiliationDate', 'birthDate', 'treatmentDate', 'startDate', 'endDate', 'issueDate', 'validUntil', 'paymentDate'].includes(key)) return this.i18n.date(String(value));
    return ['status', 'type', 'relationshipType', 'paymentMethod'].includes(key) ? this.i18n.text(String(value)) : String(value);
  }

  openMemberDetails(member: RecordData): void {
    this.closeMemberDetails();
    this.error.set('');
    this.memberDetail.set(member);
  }

  closeMemberDetails(): void {
    this.memberDetailRevision++;
    this.memberDetailsExpanded.set(false);
    this.dependentDetailsExpanded.set(false);
    this.dependentRecordTab.set('claims');
    this.dependentDetail.set(null);
    this.letterDetail.set(null);
    this.cancelIssuedGuaranteeLines();
    this.memberDetail.set(null);
    this.memberTab.set('dependents');
    this.memberClaims.set([]);
    this.memberClaimsBusy.set(false);
    this.memberClaimsLoaded.set(false);
    this.memberClaimsError.set('');
    this.memberLetters.set([]);
    this.memberLettersBusy.set(false);
    this.memberLettersLoaded.set(false);
    this.memberLettersError.set('');
    this.memberSalaries.set([]);
    this.memberContributions.set([]);
    this.memberContributionsBusy.set(false);
    this.memberContributionsLoaded.set(false);
    this.memberContributionsError.set('');
    this.pendingContributionPayment.set(null);
    this.memberContributionPaying.set(null);
    this.memberContributionPaymentError.set('');
    this.memberSalariesBusy.set(false);
    this.memberSalariesLoaded.set(false);
    this.memberSalariesError.set('');
    this.memberSalarySaving.set(false);
    this.memberSalaryError.set('');
    this.memberSalaryFormOpen.set(false);
    this.resetMemberSalaryDraft();
  }

  async selectMemberTab(tab: 'dependents' | 'claims' | 'letters' | 'contributions' | 'salaries'): Promise<void> {
    if (this.session()?.role === 'COMPANY' && !['dependents', 'contributions', 'salaries'].includes(tab)) return;
    this.memberTab.set(tab);
    if (tab === 'claims' && !this.memberClaimsLoaded()) await this.loadMemberClaims();
    if (tab === 'letters' && !this.memberLettersLoaded()) await this.loadMemberLetters();
    if (tab === 'salaries' && !this.memberSalariesLoaded()) await this.loadMemberSalaries();
    if (tab === 'contributions' && !this.memberContributionsLoaded()) await this.loadMemberContributions();
  }

  memberTabKey(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs: readonly ('dependents' | 'claims' | 'letters' | 'contributions' | 'salaries')[] = this.session()?.role === 'COMPANY'
      ? ['dependents', 'contributions', 'salaries'] : ['dependents', 'claims', 'letters', 'contributions', 'salaries'];
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (tabs.indexOf(this.memberTab()) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[index];
    void this.selectMemberTab(tab);
    (event.currentTarget as HTMLElement).parentElement?.querySelector<HTMLButtonElement>(`#member-tab-${tab}`)?.focus();
  }

  selectDependentRecordTab(tab: 'claims' | 'letters'): void {
    this.dependentRecordTab.set(tab);
  }

  dependentRecordTabKey(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = ['claims', 'letters'] as const;
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (tabs.indexOf(this.dependentRecordTab()) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const tab = tabs[index];
    this.selectDependentRecordTab(tab);
    (event.currentTarget as HTMLElement).parentElement?.querySelector<HTMLButtonElement>(`#dependent-record-tab-${tab}`)?.focus();
  }

  async loadMemberClaims(): Promise<void> {
    const member = this.memberDetail();
    if (!member?.id || this.memberClaimsBusy()) return;
    const revision = this.memberDetailRevision;
    this.memberClaimsBusy.set(true);
    this.memberClaimsError.set('');
    try {
      const claims = await this.request<RecordData[]>('GET', `/health-claims?memberId=${member.id}`);
      if (revision === this.memberDetailRevision) {
        this.memberClaims.set(claims);
        this.memberClaimsLoaded.set(true);
      }
    } catch {
      if (revision === this.memberDetailRevision) this.memberClaimsError.set('Unable to load claims.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberClaimsBusy.set(false);
    }
  }

  async loadMemberContributions(force = false): Promise<void> {
    const member = this.memberDetail();
    if (!member?.id || this.memberContributionsBusy() || this.memberContributionPaying() !== null || (this.memberContributionsLoaded() && !force)) return;
    const revision = this.memberDetailRevision;
    this.memberContributionsBusy.set(true);
    this.memberContributionsError.set('');
    try {
      const contributions = await this.request<RecordData[]>('GET', `/members/${member.id}/contributions`);
      if (revision === this.memberDetailRevision) {
        this.memberContributions.set(contributions);
        this.memberContributionsLoaded.set(true);
      }
    } catch {
      if (revision === this.memberDetailRevision) this.memberContributionsError.set('Unable to load member contributions.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberContributionsBusy.set(false);
    }
  }

  contributionOutstanding(row: RecordData): number {
    return Math.max(0, Number(row['memberAmount'] ?? 0) + Number(row['companyAmount'] ?? 0) - Number(row['paidAmount'] ?? 0));
  }

  canMarkContributionPaid(row: RecordData): boolean {
    return this.canPayContributions() && !!row.id && ['PENDING', 'PARTIALLY_PAID'].includes(String(row['status']));
  }

  canCancelContributionPayment(row: RecordData): boolean {
    return this.canPayContributions() && !!row.id && row['status'] === 'PAID';
  }

  canCancelContribution(row: RecordData): boolean {
    return this.canPayContributions() && !!row.id && row['status'] === 'PENDING' && Number(row['paidAmount'] ?? 0) === 0;
  }

  requestContributionPayment(row: RecordData): void {
    const memberId = this.memberDetail()?.id;
    if (!memberId || !this.canMarkContributionPaid(row) || this.busy() || this.memberContributionsBusy() || this.memberContributionPaying() !== null) return;
    this.memberContributionPaymentError.set('');
    this.pendingContributionPayment.set({ memberId, contribution: row });
  }

  requestContributionPaymentCancellation(row: RecordData): void {
    const memberId = this.memberDetail()?.id;
    if (!memberId || !this.canCancelContributionPayment(row) || this.busy() || this.memberContributionsBusy() || this.memberContributionPaying() !== null) return;
    this.memberContributionPaymentError.set('');
    this.pendingContributionPayment.set({ memberId, contribution: row, cancel: true });
  }

  requestContributionCancellation(row: RecordData): void {
    const memberId = this.memberDetail()?.id;
    if (!memberId || !this.canCancelContribution(row) || this.busy() || this.memberContributionsBusy() || this.memberContributionPaying() !== null) return;
    this.memberContributionPaymentError.set('');
    this.pendingContributionPayment.set({ memberId, contribution: row, cancelContribution: true });
  }

  async cancelMemberContribution(): Promise<void> {
    await this.updateMemberContributionPayment(false, true);
  }

  async payMemberContribution(): Promise<void> {
    await this.updateMemberContributionPayment(false);
  }

  async cancelMemberContributionPayment(): Promise<void> {
    await this.updateMemberContributionPayment(true);
  }

  private async updateMemberContributionPayment(cancel: boolean, cancelContribution = false): Promise<void> {
    const pending = this.pendingContributionPayment();
    if (!pending || !!pending.cancel !== cancel || !!pending.cancelContribution !== cancelContribution || pending.memberId !== this.memberDetail()?.id
      || !(cancelContribution ? this.canCancelContribution(pending.contribution) : cancel ? this.canCancelContributionPayment(pending.contribution) : this.canMarkContributionPaid(pending.contribution))
      || this.memberContributionPaying() !== null || this.busy()) return;
    const revision = this.memberDetailRevision;
    this.memberContributionPaying.set(pending.contribution.id!);
    this.memberContributionPaymentError.set('');
    this.notice.set('');
    try {
      const action = cancelContribution ? 'cancel' : cancel ? 'cancel-payment' : 'pay';
      const result = await this.request<RecordData>('POST', `/members/${pending.memberId}/contributions/${pending.contribution.id}/${action}`);
      if (revision === this.memberDetailRevision) {
        this.memberContributions.update(rows => rows.map(row => row.id === result.id ? result : row));
        this.pendingContributionPayment.set(null);
        this.notice.set(cancelContribution ? 'Contribution cancelled.' : cancel ? 'Contribution payment cancelled.' : 'Contribution marked as paid.');
      }
    } catch {
      if (revision === this.memberDetailRevision) this.memberContributionPaymentError.set(cancelContribution
        ? 'Unable to cancel contribution. Refresh and try again.' : cancel
          ? 'Unable to cancel contribution payment. Refresh and try again.' : 'Unable to record contribution payment. Refresh and try again.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberContributionPaying.set(null);
    }
  }

  async loadMemberSalaries(force = false): Promise<void> {
    const member = this.memberDetail();
    if (!member?.id || this.memberSalariesBusy() || (this.memberSalariesLoaded() && !force)) return;
    const revision = this.memberDetailRevision;
    this.memberSalariesBusy.set(true);
    this.memberSalariesError.set('');
    try {
      const salaries = await this.request<RecordData[]>('GET', `/members/${member.id}/monthly-salaries`);
      if (revision === this.memberDetailRevision) {
        this.memberSalaries.set(salaries);
        this.memberSalariesLoaded.set(true);
      }
    } catch {
      if (revision === this.memberDetailRevision) this.memberSalariesError.set('Unable to load member salaries.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberSalariesBusy.set(false);
    }
  }

  editMemberSalary(salary: RecordData): void {
    if (!salary.id || this.memberSalarySaving()) return;
    this.memberSalaryFormOpen.set(true);
    this.memberSalaryEditing.set(salary.id);
    this.memberSalaryError.set('');
    this.memberSalaryDraft.periodYear = Number(salary['periodYear']);
    this.memberSalaryDraft.periodMonth = Number(salary['periodMonth']);
    this.memberSalaryDraft.grossSalary = Number(salary['grossSalary']);
  }

  cancelMemberSalaryEdit(): void {
    if (this.memberSalarySaving()) return;
    this.memberSalaryError.set('');
    this.memberSalaryFormOpen.set(false);
    this.resetMemberSalaryDraft();
  }

  openNewMemberSalary(): void {
    if (this.memberSalarySaving()) return;
    this.memberSalaryError.set('');
    this.resetMemberSalaryDraft();
    this.memberSalaryFormOpen.set(true);
  }

  async saveMemberSalary(): Promise<void> {
    const member = this.memberDetail();
    if (!member?.id || this.memberSalarySaving()) return;
    const revision = this.memberDetailRevision;
    const editingId = this.memberSalaryEditing();
    this.memberSalarySaving.set(true);
    this.memberSalaryError.set('');
    try {
      const path = `/members/${member.id}/monthly-salaries${editingId === null ? '' : `/${editingId}`}`;
      const salary = await this.request<RecordData>(editingId === null ? 'POST' : 'PUT', path, {
        memberId: member.id,
        ...this.memberSalaryDraft,
      });
      if (revision === this.memberDetailRevision && this.memberDetail()?.id === member.id) {
        this.memberSalaries.update(salaries => this.sortSalaries(editingId === null
          ? [...salaries, salary] : salaries.map(existing => existing.id === salary.id ? salary : existing)));
        this.memberSalariesLoaded.set(true);
        this.memberSalaryFormOpen.set(false);
        this.resetMemberSalaryDraft();
      }
    } catch (error) {
      if (revision === this.memberDetailRevision) {
        this.memberSalaryError.set(error instanceof HttpErrorResponse && error.status === 409
          ? 'Monthly salary already exists for this member and period.'
          : editingId === null ? 'Unable to add monthly salary.' : 'Unable to update monthly salary.');
      }
    } finally {
      if (revision === this.memberDetailRevision) this.memberSalarySaving.set(false);
    }
  }

  private resetMemberSalaryDraft(): void {
    this.memberSalaryEditing.set(null);
    this.memberSalaryDraft.periodYear = new Date().getFullYear();
    this.memberSalaryDraft.periodMonth = new Date().getMonth() + 1;
    this.memberSalaryDraft.grossSalary = null;
  }

  claimBeneficiary(claim: RecordData, member: RecordData): string {
    if (claim['dependentId'] == null) return this.i18n.text('Member');
    const dependent = this.dependents(member).find(dependent => dependent.id === claim['dependentId']);
    return dependent ? this.cell(dependent, 'name') : `${this.i18n.text('Dependent')} #${claim['dependentId']}`;
  }

  async loadMemberLetters(): Promise<void> {
    const member = this.memberDetail();
    if (!member?.id || this.memberLettersBusy()) return;
    const revision = this.memberDetailRevision;
    this.memberLettersBusy.set(true);
    this.memberLettersError.set('');
    try {
      const letters = await this.request<RecordData[]>('GET', `/members/${member.id}/guarantee-letters`);
      if (revision === this.memberDetailRevision) {
        this.memberLetters.set(letters);
        this.memberLettersLoaded.set(true);
      }
    } catch {
      if (revision === this.memberDetailRevision) this.memberLettersError.set('Unable to load guarantee letters.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberLettersBusy.set(false);
    }
  }

  async printGuaranteeLetter(letter: RecordData, member: RecordData): Promise<void> {
    if (!letter.id || !member.id || this.printingLetter() != null) return;
    const revision = this.memberDetailRevision;
    this.printingLetter.set(letter.id);
    this.error.set('');
    try {
      const blob = await firstValueFrom(this.http.get(`/api/v1/members/${member.id}/guarantee-letters/${letter.id}/pdf`, {
        headers: new HttpHeaders({ Authorization: this.authorization }), responseType: 'blob',
      }));
      if (revision !== this.memberDetailRevision) return;
      const url = URL.createObjectURL(blob);
      const link = this.document.createElement('a');
      link.href = url;
      link.download = `guarantee-letter-${String(letter['reference'] ?? letter.id).replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`;
      this.document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
      if (letter['status'] === 'DRAFT') {
        this.memberLetters.update(letters => letters.map(item => item.id === letter.id ? { ...item, status: 'ISSUED' } : item));
      }
    } catch {
      if (revision === this.memberDetailRevision) this.error.set('Unable to download guarantee letter PDF.');
    } finally {
      this.printingLetter.set(null);
    }
  }

  async cancelGuaranteeLetter(letter: RecordData, member: RecordData): Promise<void> {
    if (!letter.id || !member.id || !['DRAFT', 'VALIDATED'].includes(String(letter['status']))
      || this.memberLettersBusy() || (letter['status'] === 'VALIDATED' && !this.canCancelValidatedGuaranteeLetter(letter))) return;
    const revision = this.memberDetailRevision;
    this.memberLettersBusy.set(true);
    try {
      const cancelled = await this.request<RecordData>('POST', `/members/${member.id}/guarantee-letters/${letter.id}/cancel`);
      if (revision === this.memberDetailRevision) {
        this.memberLetters.update(letters => letters.map(item => item.id === letter.id ? cancelled : item));
        this.memberPortal.update(portal => portal ? {
          ...portal, guaranteeLetters: portal.guaranteeLetters.map(item => item.id === cancelled.id ? cancelled : item),
        } : portal);
        if (this.letterDetail()?.letter.id === cancelled.id) this.letterDetail.set({ member, letter: cancelled });
      }
    } catch {
      if (revision === this.memberDetailRevision) this.error.set('Unable to cancel guarantee letter.');
    } finally {
      if (revision === this.memberDetailRevision) this.memberLettersBusy.set(false);
    }
  }

  lines(row: RecordData): RecordData[] { return row['lines'] as RecordData[] ?? []; }
  dependents(member: RecordData): RecordData[] { return member['dependents'] as RecordData[] ?? []; }
  async manageDependents(member: RecordData): Promise<void> {
    this.memberId = member.id ?? null;
    this.closeMemberDetails();
    await this.select('dependents');
  }
  money(value: unknown): string { return value === null || value === undefined ? '-' : this.i18n.money(Number(value)); }
  today(): string { const date = new Date(); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
  formatReportDate(value: string): string {
    if (!value) return '';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  addLine(): void { this.claimLines.push({ itemType: 'treatment', paymentDestination: 'MEMBER', billedAmount: null }); }
  changeGuaranteeLetterType(type: string): void {
    if (!['TREATMENT', 'PRESCRIPTION'].includes(type)) return;
    this.draft['type'] = type;
    this.draft['healthcareProviderType'] = null;
    this.draft['healthcareProviderId'] = null;
    const itemType = type === 'PRESCRIPTION' ? 'drug' : 'treatment';
    this.claimLines = this.claimLines.map(line => ({ ...line, itemType, itemCode: null }));
  }
  changeHealthcareProviderType(type: string | null): void {
    this.draft['healthcareProviderType'] = type || null;
    this.draft['healthcareProviderId'] = null;
  }
  guaranteeLineTotal(): number { return this.claimLines.reduce((total, line) => total + Number(line['guaranteedAmount'] ?? 0), 0); }
  contractGuarantees(): RecordData[] {
    const siret = String(this.draft['companySiret'] ?? '').trim();
    return siret ? (this.lookups()['guarantees'] ?? []).filter(guarantee => guarantee['companySiret'] === siret) : [];
  }
  changeContractCompany(): void {
    const availableIds = new Set(this.contractGuarantees().map(guarantee => guarantee.id));
    this.draft['guaranteeIds'] = (this.draft['guaranteeIds'] as number[] ?? []).filter(id => availableIds.has(id));
  }
  toggleGuarantee(id: number | undefined, checked: boolean): void {
    if (!id || (checked && !this.contractGuarantees().some(guarantee => guarantee.id === id))) return;
    const ids = this.draft['guaranteeIds'] as number[] ?? [];
    this.draft['guaranteeIds'] = checked ? [...new Set([...ids, id])] : ids.filter(value => value !== id);
  }
  hasGuarantee(id: number | undefined): boolean { return (this.draft['guaranteeIds'] as number[] ?? []).includes(id!); }
  setQuery(value: string): void { this.query.set(value); this.page.set(1); }
  async setReportStartDate(value: string): Promise<void> {
    this.reportStartDateText.set(value);
    if (!this.updateReportDatesFromText()) return;
    this.page.set(1);
    await this.refresh();
  }
  async setReportEndDate(value: string): Promise<void> {
    this.reportEndDateText.set(value);
    if (!this.updateReportDatesFromText()) return;
    this.page.set(1);
    await this.refresh();
  }

  async setReportStartDateFromPicker(value: string): Promise<void> {
    if (value) await this.setReportStartDate(this.formatReportDate(value));
  }

  async setReportEndDateFromPicker(value: string): Promise<void> {
    if (value) await this.setReportEndDate(this.formatReportDate(value));
  }

  openReportDatePicker(picker: HTMLInputElement): void {
    const nativePicker = picker as HTMLInputElement & { showPicker?: () => void };
    if (nativePicker.showPicker) nativePicker.showPicker();
    else picker.click();
  }

  async downloadReportPdf(): Promise<void> {
    if (!this.reportDateKey() || this.busy() || this.downloadingReport() || this.reportDateError()) return;
    const path = this.path();
    const queryIndex = path.indexOf('?');
    const reportPath = queryIndex < 0 ? path : path.slice(0, queryIndex);
    const query = queryIndex < 0 ? '' : path.slice(queryIndex);
    this.downloadingReport.set(true);
    this.error.set('');
    try {
      const blob = await firstValueFrom(this.http.get(`/api/v1${reportPath}/pdf${query}`, {
        headers: new HttpHeaders({ Authorization: this.authorization }), responseType: 'blob',
      }));
      const url = URL.createObjectURL(blob);
      const link = this.document.createElement('a');
      link.href = url;
      link.download = `${this.section().key.replace(/^report-/, '')}.pdf`;
      this.document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
    } catch (error) {
      this.showError(error);
    } finally {
      this.downloadingReport.set(false);
    }
  }

  async downloadContributionReportPdf(): Promise<void> {
    if (!this.canViewContributionReport() || this.contributionReportBusy() || this.downloadingReport()) return;
    const periodYear = this.contributionReportYear();
    const periodMonth = this.contributionReportMonth();
    this.downloadingReport.set(true);
    this.error.set('');
    try {
      const blob = await firstValueFrom(this.http.get(
        `/api/v1/health-contributions/report/pdf?periodYear=${periodYear}&periodMonth=${periodMonth}`,
        { headers: new HttpHeaders({ Authorization: this.authorization }), responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      const link = this.document.createElement('a');
      link.href = url;
      link.download = `contribution-report-${periodYear}-${String(periodMonth).padStart(2, '0')}.pdf`;
      this.document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
    } catch (error) {
      this.showError(error);
    } finally {
      this.downloadingReport.set(false);
    }
  }

  private updateReportDatesFromText(): boolean {
    const startDate = this.parseReportDate(this.reportStartDateText());
    const endDate = this.parseReportDate(this.reportEndDateText());
    if (startDate === null || endDate === null || (startDate && endDate && startDate > endDate)) {
      this.reportDateError.set('Enter valid dates in dd/mm/yyyy order.');
      return false;
    }
    this.reportStartDate.set(startDate);
    this.reportEndDate.set(endDate);
    this.reportDateError.set('');
    return true;
  }

  private parseReportDate(value: string): string | null {
    if (!value.trim()) return '';
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!match) return null;
    const [, day, month, year] = match;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (parsed.getUTCFullYear() !== Number(year) || parsed.getUTCMonth() !== Number(month) - 1
      || parsed.getUTCDate() !== Number(day)) return null;
    return `${year}-${month}-${day}`;
  }

  private showError(error: unknown): void {
    this.errorParams.set({});
    if (error instanceof HttpErrorResponse) {
      const data = error.error;
      this.errorParams.set({ status: error.status });
      this.error.set(error.status === 401 ? 'Authentication failed. Check your username and password.' : error.status === 0 ? 'Cannot reach the API. Check that the API server is running.' : data?.detail || data?.message || 'Request failed');
    } else this.error.set(error instanceof Error ? error.message : 'An unexpected error occurred.');
  }
}
